import React, { useState } from "react";
import Loading from "../../component/Loading/Loading";
import AlertMsg from '../../component/AlertMsg/AlertMsg';
import { useSelector } from "react-redux";
import useAxiosInstance from "../../services/useAxiosInstance";
import GlassmorphicModal from "../../component/ReusableComponents/GlassmorphicModal";

const AddCommunity = () => {
  const axiosInstance = useAxiosInstance();
  const [formData, setFormData] = useState({
    communityName: "",
    address: "",
    city: "",
    state: "",
    country: "",
    zipCode: "",
    contact: "",
    president: "",
    secretary: "",
    treasurer: "",
  });

  const [alert, setAlert] = useState({ visible: false, message: '', type: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const communityId = useSelector((state) => state.auth.communityId);
  const userId = useSelector((state) => state.auth.userId);

  // Modal states
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    console.log("Form data:", formData);

    const validationErrors = validateFormData(formData);
    if (Object.keys(validationErrors).length === 0) {
      try {
        await axiosInstance.post(`/api/public/community/add`, formData);
        setModalMessage("Community added successfully!");
        setShowSuccessModal(true);
        setAlert({ visible: true, message: 'Community added successfully!', type: 'success' });
      } catch (error) {
        console.error("Error adding community:", error);
        setModalMessage("Error adding community. Please try again.");
        setShowSuccessModal(true);
        setAlert({ visible: true, message: 'Error adding community. Please try again.', type: 'error' });
      }
    } else {
      setErrors(validationErrors);
      setAlert({ visible: true, message: 'Please correct the errors and try again.', type: 'error' });
    }

    setLoading(false);
  };

  const validateFormData = (data) => {
    let errors = {};

    if (!data.communityName) {
      errors.communityName = "Community name is required";
    } else if (data.communityName.length > 20) {
      errors.communityName = "Community name must be 20 characters or less";
    }

    if (!data.address) {
      errors.address = "Address is required";
    }

    if (!data.city) {
      errors.city = "City is required";
    }

    if (!data.state) {
      errors.state = "State is required";
    }

    if (!data.country) {
      errors.country = "Country is required";
    }

    if (!data.zipCode) {
      errors.zipCode = "Zip code is required";
    }

    if (!data.contact) {
      errors.contact = "Contact is required";
    } else if (!isValidPhoneNumber(data.contact)) {
      errors.contact = "Invalid phone number format (e.g., +1 215-555-1212)";
    }

    if (!data.president) {
      errors.president = "President name is required";
    } else if (data.president.length > 10) {
      errors.president = "President name must be 10 characters or less";
    }

    if (!data.secretary) {
      errors.secretary = "Secretary name is required";
    } else if (data.secretary.length > 10) {
      errors.secretary = "Secretary name must be 10 characters or less";
    }

    if (!data.treasurer) {
      errors.treasurer = "Treasurer name is required";
    } else if (data.treasurer.length > 10) {
      errors.treasurer = "Treasurer name must be 10 characters or less";
    }

    return errors;
  };

  const isValidPhoneNumber = (phoneNumber) => {
    const phonePattern = /^\+\d{1,3} \d{3}-\d{3}-\d{4}$/;
    return phonePattern.test(phoneNumber);
  };

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const handleConfirmCancel = () => {
    setFormData({
      communityName: "",
      address: "",
      city: "",
      state: "",
      country: "",
      zipCode: "",
      contact: "",
      president: "",
      secretary: "",
      treasurer: "",
    });
    setErrors({});
    setShowCancelModal(false);
  };

  // Cancel confirmation modal footer
  const cancelModalFooter = (
    <div className="flex justify-end space-x-2">
      <button
        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
        onClick={() => setShowCancelModal(false)}
      >
        No, Keep Editing
      </button>
      <button
        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
        onClick={handleConfirmCancel}
      >
        Yes, Clear Form
      </button>
    </div>
  );

  // Success/error modal footer
  const successModalFooter = (
    <div className="flex justify-end">
      <button
        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
        onClick={() => setShowSuccessModal(false)}
      >
        OK
      </button>
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen md:p-4">
      {loading && <Loading />}
      {!loading && (
        <div className="max-w-7xl mx-auto">
          {alert.visible && (
            <AlertMsg
              message={alert.message}
              type={alert.type}
              onClose={() => setAlert({ ...alert, visible: false })}
            />
          )}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Add Community</h1>
          </div>
          <div className="w-full">
            <form onSubmit={handleSubmit} className="flex flex-col lg:flex-row flex-wrap gap-6">
              <div className="w-full lg:w-[calc(50%-1.5rem)] bg-white rounded-lg shadow-sm p-6">
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-gray-800">Community Details</h2>
                </div>
                <div className="w-full space-y-4">
                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name of the community <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="communityName"
                      value={formData.communityName}
                      onChange={handleChange}
                      className={`w-full rounded-md border ${errors.communityName ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.communityName && <p className="text-red-500 text-sm mt-1">{errors.communityName}</p>}
                  </div>
                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      placeholder="Street Address"
                      onChange={handleChange}
                      className={`w-full rounded-md border ${errors.address ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                  </div>
                  <div className="w-full">
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      placeholder="City"
                      onChange={handleChange}
                      className={`w-full rounded-md border ${errors.city ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                  </div>
                  <div className="w-full">
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      placeholder="State"
                      onChange={handleChange}
                      className={`w-full rounded-md border ${errors.state ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
                  </div>
                  <div className="w-full">
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      placeholder="Country"
                      onChange={handleChange}
                      className={`w-full rounded-md border ${errors.country ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.country && <p className="text-red-500 text-sm mt-1">{errors.country}</p>}
                  </div>
                  <div className="w-full">
                    <input
                      type="text"
                      name="zipCode"
                      value={formData.zipCode}
                      placeholder="Zip Code"
                      onChange={handleChange}
                      className={`w-full rounded-md border ${errors.zipCode ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.zipCode && <p className="text-red-500 text-sm mt-1">{errors.zipCode}</p>}
                  </div>
                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="contact"
                      value={formData.contact}
                      onChange={handleChange}
                      className={`w-full rounded-md border ${errors.contact ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.contact && <p className="text-red-500 text-sm mt-1">{errors.contact}</p>}
                  </div>
                </div>
              </div>

              <div className="w-full lg:w-[calc(50%-1.5rem)] bg-white rounded-lg shadow-sm p-6">
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-gray-800">Board Members</h2>
                </div>
                <div className="w-full space-y-4">
                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      President <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="president"
                      value={formData.president}
                      onChange={handleChange}
                      className={`w-full rounded-md border ${errors.president ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.president && <p className="text-red-500 text-sm mt-1">{errors.president}</p>}
                  </div>
                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Secretary <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="secretary"
                      value={formData.secretary}
                      onChange={handleChange}
                      className={`w-full rounded-md border ${errors.secretary ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.secretary && <p className="text-red-500 text-sm mt-1">{errors.secretary}</p>}
                  </div>
                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Treasurer <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="treasurer"
                      value={formData.treasurer}
                      onChange={handleChange}
                      className={`w-full rounded-md border ${errors.treasurer ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.treasurer && <p className="text-red-500 text-sm mt-1">{errors.treasurer}</p>}
                  </div>
                </div>
              </div>

              <div className="w-full flex items-center justify-between mt-2 mb-6">
                <div className="text-sm text-red-500">* indicates required field</div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    onClick={handleCancelClick}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  >
                    Add Community
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      <GlassmorphicModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title="Confirm Cancel"
        size="sm"
        footer={cancelModalFooter}
      >
        <p className="text-gray-700">
          Are you sure you want to cancel? All entered information will be lost.
        </p>
      </GlassmorphicModal>

      {/* Success/Error Modal */}
      <GlassmorphicModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={alert.type === 'success' ? "Success" : "Error"}
        size="sm"
        footer={successModalFooter}
      >
        <p className="text-gray-700">{modalMessage}</p>
      </GlassmorphicModal>
    </div>
  );
};

export default AddCommunity;